/**
 * FloatingProcessingIndicator - Unified Processing Status Pill
 *
 * A compact, elegant floating indicator that shows all active processes
 * in a single unified pill. Expands on hover/click to show details.
 *
 * Design: Organic minimalism with subtle animations
 * Inspired by iOS Dynamic Island concept - compact yet informative
 */

import React, { useState, useMemo } from 'react';
import { Box, Typography, Collapse, IconButton } from '@mui/material';
import { alpha, styled } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CloseIcon from '@mui/icons-material/Close';
import {
  warmSuccessPrimary,
  warmSuccessSecondary,
  warmSuccessNeutral,
} from '../theme/warmSuccessDesignSystem';
import { useProcessingTracker } from '../hooks/useProcessingTracker';
import { ActiveProcessItem } from '../types/processing.types';

// ============================================================================
// STYLED COMPONENTS
// ============================================================================

const PillContainer = styled('div')(() => ({
  position: 'fixed',
  bottom: 24,
  right: 24,
  zIndex: 9999,
}));

const PillWrapper = styled('div')<{ $expanded: boolean }>(({ $expanded }) => ({
  background: `linear-gradient(135deg, ${alpha(warmSuccessNeutral[900], 0.92)} 0%, ${alpha(warmSuccessNeutral[800], 0.95)} 100%)`,
  backdropFilter: 'blur(20px) saturate(180%)',
  borderRadius: $expanded ? 20 : 28,
  boxShadow: `
    0 4px 24px ${alpha(warmSuccessNeutral[900], 0.25)},
    0 1px 3px ${alpha(warmSuccessNeutral[900], 0.15)},
    inset 0 1px 0 ${alpha('#fff', 0.08)}
  `,
  border: `1px solid ${alpha('#fff', 0.1)}`,
  overflow: 'hidden',
  cursor: 'pointer',
  minWidth: $expanded ? 280 : 'auto',
  maxWidth: 340,
  transition: 'border-radius 0.3s ease, min-width 0.3s ease',
}));

const PillHeader = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  padding: '10px 16px',
}));

const PulsingDot = styled('div')(() => ({
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: `linear-gradient(135deg, ${warmSuccessPrimary[400]} 0%, ${warmSuccessPrimary[500]} 100%)`,
  boxShadow: `0 0 12px ${alpha(warmSuccessPrimary[500], 0.6)}`,
  flexShrink: 0,
}));

const OrbitRing = styled('div')(() => ({
  position: 'absolute',
  width: 22,
  height: 22,
  borderRadius: '50%',
  border: `1.5px solid ${alpha(warmSuccessPrimary[400], 0.3)}`,
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
}));

const CountBadge = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minWidth: 20,
  height: 20,
  borderRadius: 10,
  background: alpha(warmSuccessPrimary[500], 0.2),
  border: `1px solid ${alpha(warmSuccessPrimary[400], 0.3)}`,
  fontSize: '0.6875rem',
  fontWeight: 700,
  color: warmSuccessPrimary[300],
  fontFamily: '"DM Sans", sans-serif',
}));

const ExpandedContent = styled('div')(() => ({
  borderTop: `1px solid ${alpha('#fff', 0.08)}`,
  padding: '8px 0',
}));

const ProcessItem = styled('div')(() => ({
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '8px 16px',
  transition: 'background 0.2s ease',
  '&:hover': {
    background: alpha('#fff', 0.04),
  },
}));

const TypeIcon = styled(Box)<{ $type: 'analysis' | 'job_search' }>(({ $type }) => ({
  width: 28,
  height: 28,
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: '0.875rem',
  background: $type === 'analysis'
    ? alpha(warmSuccessPrimary[500], 0.15)
    : alpha(warmSuccessSecondary[500], 0.15),
  border: `1px solid ${$type === 'analysis' ? alpha(warmSuccessPrimary[400], 0.2) : alpha(warmSuccessSecondary[400], 0.2)}`,
  flexShrink: 0,
}));

const MiniSpinner = styled('div')(() => ({
  width: 14,
  height: 14,
  borderRadius: '50%',
  border: `2px solid ${alpha(warmSuccessPrimary[400], 0.2)}`,
  borderTopColor: warmSuccessPrimary[400],
  flexShrink: 0,
}));

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

const AnimatedPulsingIndicator: React.FC = () => (
  <Box sx={{ position: 'relative', width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <OrbitRing style={{ animation: 'fpiOrbit 2s ease-in-out infinite' }} />
    <PulsingDot style={{ animation: 'fpiDotPulse 1.5s ease-in-out infinite' }} />
  </Box>
);

const ProcessItemRow: React.FC<{ item: ActiveProcessItem; index: number }> = ({ item, index }) => (
  <ProcessItem
    style={{ animation: 'fpiItemIn 0.2s ease both', animationDelay: `${index * 0.05}s` }}
  >
    <TypeIcon $type={item.type}>
      {item.type === 'analysis' ? '🔍' : '🔎'}
    </TypeIcon>
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontSize: '0.6875rem',
          fontWeight: 600,
          color: alpha('#fff', 0.5),
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          fontFamily: '"Inter", sans-serif',
          mb: 0.25,
        }}
      >
        {item.type === 'analysis' ? 'Analyse' : 'Recherche'}
      </Typography>
      <Typography
        noWrap
        sx={{
          fontSize: '0.8125rem',
          fontWeight: 500,
          color: '#fff',
          fontFamily: '"Inter", sans-serif',
          lineHeight: 1.3,
        }}
      >
        {item.name}
      </Typography>
    </Box>
    <MiniSpinner style={{ animation: 'fpiSpin 1s linear infinite' }} />
  </ProcessItem>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

const FloatingProcessingIndicator: React.FC = () => {
  const { activeItems, analysisCount, jobSearchCount } = useProcessingTracker();
  const [isExpanded, setIsExpanded] = useState(false);
  const [isDismissed, setIsDismissed] = useState(false);

  // Group items by type
  const { analyses, searches } = useMemo(() => ({
    analyses: activeItems.filter(item => item.type === 'analysis'),
    searches: activeItems.filter(item => item.type === 'job_search'),
  }), [activeItems]);

  const totalCount = activeItems.length;

  // Reset dismissed state when new items appear
  React.useEffect(() => {
    if (totalCount > 0 && isDismissed) {
      setIsDismissed(false);
    }
  }, [totalCount, isDismissed]);

  if (totalCount === 0 || isDismissed) {
    return null;
  }

  const handleToggle = () => {
    setIsExpanded(!isExpanded);
  };

  const handleDismiss = (e: React.MouseEvent) => {
    e.stopPropagation();
    setIsDismissed(true);
    setIsExpanded(false);
  };

  // Generate summary text
  const getSummaryText = () => {
    const parts: string[] = [];
    if (analysisCount > 0) {
      parts.push(`${analysisCount} analyse${analysisCount > 1 ? 's' : ''}`);
    }
    if (jobSearchCount > 0) {
      parts.push(`${jobSearchCount} recherche${jobSearchCount > 1 ? 's' : ''}`);
    }
    return parts.join(' · ');
  };

  return (
    <>
      {/* CSS keyframes replacing the former Framer Motion animations */}
      <style>{`
        @keyframes fpiPillIn {
          from { opacity: 0; transform: translateY(30px) scale(0.8); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes fpiOrbit {
          0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
          50% { transform: translate(-50%, -50%) scale(1.4); opacity: 0; }
        }
        @keyframes fpiDotPulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.15); }
        }
        @keyframes fpiSpin { to { transform: rotate(360deg); } }
        @keyframes fpiItemIn {
          from { opacity: 0; transform: translateX(-10px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes fpiTextIn {
          from { opacity: 0; transform: translateY(4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
      <PillContainer
        style={{
          animation: 'fpiPillIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1) both',
        }}
      >
        <PillWrapper
          $expanded={isExpanded}
          onClick={handleToggle}
        >
          {/* Header - Always visible */}
          <PillHeader>
            <AnimatedPulsingIndicator />

            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                sx={{
                  fontSize: '0.8125rem',
                  fontWeight: 600,
                  color: '#fff',
                  fontFamily: '"DM Sans", sans-serif',
                  letterSpacing: '-0.01em',
                }}
              >
                En cours
              </Typography>
              <div
                key={getSummaryText()}
                style={{ animation: 'fpiTextIn 0.2s ease both' }}
              >
                <Typography
                  sx={{
                    fontSize: '0.6875rem',
                    color: alpha('#fff', 0.6),
                    fontFamily: '"Inter", sans-serif',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {getSummaryText()}
                </Typography>
              </div>
            </Box>

            {totalCount > 1 && (
              <CountBadge>{totalCount}</CountBadge>
            )}

            <div
              style={{
                display: 'inline-flex',
                transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s ease',
              }}
            >
              <KeyboardArrowDownIcon
                sx={{
                  fontSize: 18,
                  color: alpha('#fff', 0.5),
                }}
              />
            </div>

            <IconButton
              size="small"
              onClick={handleDismiss}
              sx={{
                width: 24,
                height: 24,
                ml: -0.5,
                color: alpha('#fff', 0.4),
                '&:hover': {
                  color: alpha('#fff', 0.7),
                  bgcolor: alpha('#fff', 0.1),
                },
              }}
            >
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </PillHeader>

          {/* Expanded Content */}
          <Collapse in={isExpanded} timeout={300}>
            <ExpandedContent>
              {/* Analyses Section */}
              {analyses.length > 0 && (
                <Box>
                  {analyses.length > 1 && (
                    <Typography
                      sx={{
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        color: alpha('#fff', 0.35),
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        px: 2,
                        py: 0.75,
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      Analyses ({analyses.length})
                    </Typography>
                  )}
                  {analyses.map((item, index) => (
                    <ProcessItemRow key={item.id} item={item} index={index} />
                  ))}
                </Box>
              )}

              {/* Searches Section */}
              {searches.length > 0 && (
                <Box>
                  {searches.length > 1 && (
                    <Typography
                      sx={{
                        fontSize: '0.625rem',
                        fontWeight: 700,
                        color: alpha('#fff', 0.35),
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        px: 2,
                        py: 0.75,
                        mt: analyses.length > 0 ? 1 : 0,
                        fontFamily: '"Inter", sans-serif',
                      }}
                    >
                      Recherches ({searches.length})
                    </Typography>
                  )}
                  {searches.map((item, index) => (
                    <ProcessItemRow key={item.id} item={item} index={analyses.length + index} />
                  ))}
                </Box>
              )}
            </ExpandedContent>
          </Collapse>
        </PillWrapper>
      </PillContainer>
    </>
  );
};

export default FloatingProcessingIndicator;
